import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import { useNavigate, useLocation } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CategoryIcon from "@mui/icons-material/Category";
import { sidebarStructure } from "../../navigations/sidebarConfig";
import { LIST_ROUTE_ANY_PERMISSIONS } from "../../navigations/resourceListPermissions";
import { useAuth } from "../../hooks/useAuth";
import { useSidebarRail } from "../../context/SidebarRailContext";
import { useAppTheme } from "../../context/AppThemeContext";

const SIDEBAR_TOP_DEFAULT = 72;
const SIDEBAR_TOP_IMPERSONATING = 137;

const normalizePerm = (p) =>
  (p || "").toLowerCase().replace(/\s+/g, "-").replace(/_/g, "-");
const multiPermMap = {
  "/users-list": ["user-list-front", "user-direct-permission"],
  "/customers-list": ["customer-list", "customer-list-front"],
  "/permissions/assign": ["role-view", "user-direct-permission"],
  ...LIST_ROUTE_ANY_PERMISSIONS,
};

const Sidebar = () => {
  const theme = useTheme();
  const { preset } = useAppTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));
  const primary = theme.palette.primary.main;
  const { permissions, impersonating } = useAuth();
  const sidebarTop = impersonating
    ? SIDEBAR_TOP_IMPERSONATING
    : SIDEBAR_TOP_DEFAULT;
  const navigate = useNavigate();
  const location = useLocation();
  const [activeKey, setActiveKey] = useState(location.pathname);
  const [openGroups, setOpenGroups] = useState({});
  /** sm+: collapsed/expanded width; must use context so PageLayout main column margin stays in sync. */
  const { railExpanded, setRailExpanded } = useSidebarRail();

  useEffect(() => {
    setActiveKey(location.pathname);
  }, [location.pathname]);

  const hasPerm = (perm) =>
    permissions.some((p) => normalizePerm(p) === normalizePerm(perm));
  const hasAnyPerm = (perms) =>
    perms.some((p) =>
      permissions.some((u) => normalizePerm(u) === normalizePerm(p)),
    );

  const isItemVisible = (item) => {
    if (hasPerm("super-admin")) return true;
    const multiPerms = multiPermMap[item.path];
    if (multiPerms) return hasAnyPerm(multiPerms);
    if (!item.permission) return true;
    return hasPerm(item.permission);
  };

  const toggleGroup = (label) => {
    setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const isGroupOpen = (label, children) => {
    if (openGroups[label] !== undefined) return openGroups[label];
    return children.some((c) => c.path === activeKey);
  };

  const listContent = useMemo(() => {
    const nodes = [];
    sidebarStructure.forEach((entry) => {
      if (entry.type === "item") {
        if (!isItemVisible(entry)) return;
        nodes.push({ type: "item", key: entry.path, ...entry });
        return;
      }
      if (entry.type === "group") {
        const visibleChildren = entry.children.filter((c) => isItemVisible(c));
        if (visibleChildren.length === 0) return;
        nodes.push({
          type: "group",
          key: entry.label,
          label: entry.label,
          icon: entry.icon,
          children: visibleChildren,
        });
      }
    });
    return nodes;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dependency list
  }, [permissions]);

  const handleItemClick = (path, e) => {
    setActiveKey(path);
    if (e.ctrlKey) {
      window.open(path, "_blank");
      return;
    }
    navigate(path);
  };

  const linkButtonSx = (path) => ({
    px: 2,
    py: 1.5,
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    bgcolor: activeKey === path ? "grey.100" : "white",
    border: "1px solid",
    borderColor: activeKey === path ? primary : "grey.200",
    borderLeft:
      activeKey === path ? `4px solid ${primary}` : "4px solid transparent",
    "&:hover": { bgcolor: activeKey === path ? "grey.100" : "grey.50" },
    transition: "all 0.2s ease",
  });

  /** sm+: icon rail (72px); `.sidebar-expanded` = 250px with labels. xs: fixed width with labels. */
  const sidebarRailSx = {
    width: { xs: 200, sm: 72 },
    transition:
      "width 0.22s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.22s ease",
    "& .sidebar-label": {
      transition: "opacity 0.18s ease, max-width 0.22s ease",
      whiteSpace: "nowrap",
      overflow: "hidden",
    },
    "& .sidebar-footer-text": {
      transition: "opacity 0.18s ease, max-height 0.22s ease",
    },
    "@media (min-width: 600px)": {
      "&.sidebar-expanded": {
        width: 250,
      },
      "& .sidebar-label": {
        opacity: 0,
        maxWidth: 0,
      },
      "&.sidebar-expanded .sidebar-label": {
        opacity: 1,
        maxWidth: 220,
      },
      "& .sidebar-item-btn, & .sidebar-group-btn": {
        justifyContent: "center",
        gap: 0,
      },
      "&.sidebar-expanded .sidebar-item-btn": {
        justifyContent: "flex-start",
        gap: 1.5,
      },
      "&.sidebar-expanded .sidebar-group-btn": {
        justifyContent: "space-between",
        gap: 1,
      },
      "& .sidebar-footer-text": {
        opacity: 0,
        maxHeight: 0,
        overflow: "hidden",
      },
      "&.sidebar-expanded .sidebar-footer-text": {
        opacity: 1,
        maxHeight: 56,
      },
    },
  };

  const railOpen = isSmUp && railExpanded;

  return (
    <Box
      className={railOpen ? "sidebar-expanded" : undefined}
      sx={{
        ...sidebarRailSx,
        height: `calc(100vh - ${sidebarTop}px)`,
        bgcolor: "white",
        borderRight: 1,
        borderColor: "grey.200",
        overflow: "hidden",
        position: "fixed",
        top: sidebarTop,
        left: 0,
        zIndex: 1100,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {isSmUp && (
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: railExpanded ? "flex-end" : "center",
            px: 0.5,
            py: 0.5,
            borderBottom: 1,
            borderColor: "grey.200",
            minHeight: 44,
          }}
        >
          <IconButton
            size="small"
            aria-expanded={railExpanded}
            aria-label={railExpanded ? "Collapse sidebar" : "Expand sidebar"}
            onClick={() => setRailExpanded((v) => !v)}
            sx={{ color: "grey.700", "&:hover": { bgcolor: "grey.100" } }}
          >
            {railExpanded ? <MenuOpenIcon /> : <MenuIcon />}
          </IconButton>
        </Box>
      )}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 1.5,
          py: 1,
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "#e0e0e0",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-thumb:hover": { background: "#bdbdbd" },
        }}
      >
        <List
          disablePadding
          sx={{ display: "flex", flexDirection: "column", gap: 1 }}
        >
          {listContent.map((node) => {
            if (node.type === "item") {
              return (
                <ListItem key={node.key} disablePadding sx={{ mt: 0 }}>
                  <ListItemButton
                    className="sidebar-item-btn"
                    onClick={(e) => handleItemClick(node.path, e)}
                    sx={{
                      ...linkButtonSx(node.path),
                      borderRadius: 2,
                      "&:hover": {
                        bgcolor:
                          activeKey === node.path ? "grey.100" : "grey.50",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        color:
                          activeKey === node.path ? "primary.main" : "grey.500",
                        flexShrink: 0,
                      }}
                    >
                      {node.icon}
                    </Box>
                    <Typography
                      component="span"
                      className="sidebar-label"
                      variant="body2"
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: activeKey === node.path ? 600 : 500,
                        color:
                          activeKey === node.path ? "primary.main" : "grey.700",
                      }}
                    >
                      {node.label}
                    </Typography>
                  </ListItemButton>
                </ListItem>
              );
            }
            const open = isGroupOpen(node.label, node.children);
            return (
              <Box key={node.key} sx={{ mt: 0.5 }}>
                <ListItem disablePadding>
                  <ListItemButton
                    className="sidebar-group-btn"
                    onClick={() => toggleGroup(node.label)}
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      px: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      bgcolor: "white",
                      color: "grey.800",
                      border: "1px solid",
                      borderColor: "grey.200",
                      "&:hover": {
                        background: preset.gradient,
                        color: "white",
                        borderColor: "transparent",
                        "& .group-icon": { color: "white" },
                        "& .group-chevron": { color: "white" },
                      },
                      ...(open && {
                        background: preset.gradient,
                        color: "white",
                        borderColor: "transparent",
                        "& .group-icon": { color: "white" },
                        "& .group-chevron": { color: "white" },
                      }),
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        minWidth: 0,
                      }}
                    >
                      <Box
                        className="group-icon"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          color: open ? "white" : "grey.600",
                          flexShrink: 0,
                        }}
                      >
                        {node.icon ?? <CategoryIcon sx={{ fontSize: 20 }} />}
                      </Box>
                      <Typography
                        component="span"
                        className="sidebar-label"
                        variant="body2"
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: 700,
                          color: "inherit",
                        }}
                      >
                        {node.label}
                      </Typography>
                    </Box>
                    <Box
                      className="group-chevron sidebar-label"
                      component="span"
                      sx={{
                        color: open ? "white" : "grey.600",
                        flexShrink: 0,
                        display: "flex",
                      }}
                    >
                      {open ? (
                        <ExpandLessIcon sx={{ fontSize: 20 }} />
                      ) : (
                        <ExpandMoreIcon sx={{ fontSize: 20 }} />
                      )}
                    </Box>
                  </ListItemButton>
                </ListItem>
                {open && (
                  <Box
                    sx={{
                      pl: 1.5,
                      pt: 0.5,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                    }}
                  >
                    {node.children.map((child) => (
                      <ListItem key={child.path} disablePadding>
                        <ListItemButton
                          className="sidebar-item-btn"
                          onClick={(e) => handleItemClick(child.path, e)}
                          sx={{
                            borderRadius: 2,
                            bgcolor: "white",
                            border: "1px solid",
                            borderColor: "grey.200",
                            py: 1.25,
                            pl: 2,
                            pr: 2,
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                            borderLeft:
                              activeKey === child.path
                                ? `4px solid ${primary}`
                                : "4px solid transparent",
                            "&:hover": {
                              bgcolor: "grey.50",
                              borderColor: "grey.300",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              color:
                                activeKey === child.path
                                  ? "primary.main"
                                  : "grey.600",
                              flexShrink: 0,
                            }}
                          >
                            {child.icon}
                          </Box>
                          <Typography
                            component="span"
                            className="sidebar-label"
                            variant="body2"
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: activeKey === child.path ? 600 : 500,
                              color:
                                activeKey === child.path
                                  ? "primary.main"
                                  : "grey.700",
                            }}
                          >
                            {child.label}
                          </Typography>
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </List>
      </Box>

      <Box
        className="sidebar-footer-text"
        sx={{
          px: 2,
          py: 3,
          color: "grey.500",
          fontSize: "0.75rem",
          borderTop: "1px solid",
          borderColor: "grey.200",
          backgroundColor: "white",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="caption">Powered by</Typography>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: "primary.main" }}
          >
            INOVIOTECH INC
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Sidebar;
